import { ReactNode, useEffect } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'store'
import { getDeliveryTickets, getMaterialReceiptSiteConfig } from 'features/materialReceipt/materialReceiptSlice'
import { NavBar } from 'components/NavBar/NavBar'
import { Flex, PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { BodyText, Head1, OtherText, TitleText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { MaterialReceiptStation } from 'types/materialReceipt.interface'
import { useMaterialReceiptStation } from './materialReceiptAccess'
import { NoAccessIcon, NoAccessWrap, TabScroller } from './MaterialReceipt.styled'

const MANAGER_TABS = [
  { to: '/material-receipt/dashboard', label: 'Dashboard' },
  { to: '/material-receipt/deliveries', label: 'Deliveries' },
  { to: '/material-receipt/cases', label: 'Cases' },
  { to: '/material-receipt/audit', label: 'Audit trail' },
  { to: '/material-receipt/config', label: 'Site config' },
]

const LAB_TABS = [
  { to: '/material-receipt/my-work', label: 'My work' },
  { to: '/material-receipt/deliveries', label: 'Deliveries' },
]

export const MaterialReceiptNav = ({ station }: { station: MaterialReceiptStation }) => {
  const tabs = station === 'MANAGER' ? MANAGER_TABS : LAB_TABS
  const { pathname } = useLocation()
  const navigate = useNavigate()
  return (
    <TabScroller>
      <TabsContainer>
        {tabs.map((t) => (
          <Tabs key={t.to} active={pathname === t.to} onClick={() => navigate(t.to)}>
            {t.label}
          </Tabs>
        ))}
      </TabsContainer>
    </TabScroller>
  )
}

export const MaterialReceiptNoAccess = () => (
  <>
    <NavBar />
    <PageWrapper>
      <NoAccessWrap>
        <NoAccessIcon>🔒</NoAccessIcon>
        <TitleText weight='bold'>Material Receipt is restricted</TitleText>
        <BodyText grey>This area is available to the Lab and Manager roles.</BodyText>
      </NoAccessWrap>
    </PageWrapper>
  </>
)

export const MaterialReceiptPage = ({
  requireManager,
  hideNav,
  title,
  subtitle,
  children,
}: {
  requireManager?: boolean
  hideNav?: boolean
  title?: ReactNode
  subtitle?: ReactNode
  children: (station: MaterialReceiptStation) => ReactNode
}) => {
  const station = useMaterialReceiptStation()
  const dispatch = useDispatch<AppDispatch>()

  useEffect(() => {
    dispatch(getDeliveryTickets({}))
    dispatch(getMaterialReceiptSiteConfig())
  }, [dispatch])

  if (!station) return <MaterialReceiptNoAccess />
  if (requireManager && station !== 'MANAGER') return <Navigate to='/material-receipt/my-work' replace />
  return (
    <>
      <NavBar />
      <PageWrapper>
        {(title || subtitle) && (
          <Flex between center mb='16'>
            {title && (
              <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>
                {title}
              </Head1>
            )}
            {subtitle && <OtherText grey>{subtitle}</OtherText>}
          </Flex>
        )}
        {!hideNav && <MaterialReceiptNav station={station} />}
        {children(station)}
      </PageWrapper>
    </>
  )
}

export const MaterialReceiptLanding = () => {
  const station = useMaterialReceiptStation()
  if (!station) return <MaterialReceiptNoAccess />
  return <Navigate to={station === 'LAB' ? '/material-receipt/my-work' : '/material-receipt/dashboard'} replace />
}
